import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { intervalToDuration } from 'date-fns';
import { Typography } from '@mui/material';
import { DataGridPro, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { getReportJobs, getReportJobsCount } from '@/generated/client/reports/reports';
import { ReportJobDto } from '@/generated/model';
import { SearchComponent } from '@/components/common/Search';
import SearchNoResult from '@/shared/ui/no-result-display/SearchNoResult';
import { ctxReport } from '@/contexts/ReportCtx';
import { fulldate } from '@/utilities/dateUtils';

import './OverviewTab.css';

export default function OverviewTab() {
  const { t } = useTranslation();
  const reportCtx = useContext(ctxReport);
  const [searchText, setSearchText] = useState<string | undefined>();

  const [rows, setRows] = useState<ReportJobDto[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [filterModel] = useState({ items: [] });
  const [sortModel] = useState([]);

  async function loadRows() {
    let localSearchText = searchText;
    if (!localSearchText) {
      localSearchText = '';
    }
    const dates = getDates();
    const reports = await getReportJobs({
      searchTerm: localSearchText,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      fromTime: dates[0],
      toTime: dates[1],
    });
    setRows(reports.data);
  }

  function getDates() {
    const selectedDates = reportCtx?.selectedDates;
    if (!selectedDates) {
      return [undefined, undefined];
    }
    const from = selectedDates.from;
    const to = selectedDates.to;
    return [from ? from.getTime() : undefined, to ? to.getTime() : undefined];
  }

  useEffect(() => {
    void loadRows();
  }, [paginationModel, sortModel, filterModel, searchText, reportCtx?.selectedDates]);

  useEffect(() => {
    async function loadCount() {
      let localSearchText = searchText;
      if (!localSearchText) {
        localSearchText = '';
      }
      const dates = getDates();
      const rowCount = await getReportJobsCount({
        searchTerm: localSearchText,
        fromTime: dates[0],
        toTime: dates[1],
      });
      setRowCount(rowCount.data);
    }
    void loadCount();
  }, [searchText, reportCtx?.selectedDates]);

  const columns = [
    { field: 'code', headerName: t('report.overview.column.code'), flex: 0.5 },
    { field: 'name', headerName: t('report.overview.column.name'), flex: 1 },
    {
      field: 'startTimeMillis',
      headerName: t('report.overview.column.startTime'),
      flex: 1,
      valueFormatter: (value?: number) => {
        if (!value) {
          return '';
        }
        return fulldate(value * 1000, t);
      },
    },
    {
      field: 'runTimeMillis',
      headerName: t('report.overview.column.runTime'),
      flex: 0.5,
      valueFormatter: (value?: number) => {
        if (!value) {
          return '';
        }
        if (value < 1000) {
          return `<1${t('general.dateTime.second')}`;
        }
        const duration = intervalToDuration({ start: 0, end: value });
        const minutes = duration.minutes;
        const seconds = duration.seconds;
        let result = '';
        if (minutes) {
          result += `${minutes}${t('general.dateTime.minute')}`;
        }
        if (seconds) {
          result += ` ${seconds}${t('general.dateTime.second')}`;
        }
        return result.trim();
      },
    },
    {
      field: 'state',
      headerName: t('report.overview.column.state'),
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => {
        const state = params.row.state;
        return (
          <div className="report-job-state-container">
            <div className={'report-job-state-indicator report-job-state-' + state}></div>
            <Typography variant="body2">{t('report.overview.state.' + state)}</Typography>
          </div>
        );
      },
    },
    { field: 'email', headerName: t('report.overview.column.email'), flex: 1 },
  ];

  return (
    <>
      <Typography variant="h6" className="report-overview-text">
        {t('report.reportChooser')}
      </Typography>
      <div className="report-overview-search">
        <SearchComponent
          searchText={searchText}
          setSearchText={setSearchText}
          showHistory={false}
          placeholder={t('report.searchPlaceholder')}
          refreshFunction={loadRows}
        />
      </div>
      <DataGridPro
        className="report-overview-grid"
        rows={rows}
        columns={columns}
        pagination
        filterMode="server"
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        rowCount={rowCount}
        disableColumnFilter
        disableColumnMenu
        pageSizeOptions={[100]}
        // onSortModelChange={setSortModel}
        // onFilterModelChange={setFilterModel}
        slotProps={{
          pagination: {
            labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
              `${from}–${to} / ${count}`,
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <SearchNoResult searchText={searchText} onClearSearch={() => setSearchText(undefined)} />
          ),
        }}
        localeText={{
          footerRowSelected: (count: number) => t('filter.rowSelected', { count }),
        }}
        style={{ width: '100%' }}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiDataGrid-footerContainer': {
            boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
        }}
      />
    </>
  );
}
